import styled from 'styled-components';
import { Base, Theme } from 'styled';
import { Props, State } from './index';

type ContainerProps = Pick<Props, 'renderMinimalNav'>;

type TopBarProps = Pick<Props, 'renderMinimalNav'>;

type TopBarMenuButtonContainerProps = Pick<State, 'menuIsActive'>;

type NavProps = Pick<
  Props,
  'notificationBarIsActive' | 'notificationBarHeight' | 'currentBreakpoint'
> &
  Pick<State, 'menuIsActive'>;

type MainMenuContainerProps = Pick<
  Props,
  'notificationBarIsActive' | 'notificationBarHeight' | 'currentBreakpoint'
>;

type MainMenuButtonProps = {
  isCurrentPath?: boolean;
};

type MainMenuSubNavProps = Pick<State, 'sublistHeights'> & {
  sectionSlug: string;
  sectionIsExpanded: boolean;
  breakpointIsMdUp: boolean;
};

export const Container = styled(Base)<ContainerProps>`
  position: fixed;
  ${({ theme }) => theme.zIndex('nav')};
  pointer-events: none;
  width: 100%;

  ${Theme.mediaQueries.md} {
    width: ${({ theme, renderMinimalNav }) =>
      !renderMinimalNav && theme.sizes.sideNavWidthDesktop};
    border-right: ${({ theme, renderMinimalNav }) =>
      !renderMinimalNav && theme.borders['gray-lighter']};
  }
`;

export const TopBar = styled(Base).withConfig({
  shouldForwardProp: (prop, defaultValidatorFn) =>
    !['display'].includes(prop) && defaultValidatorFn(prop),
})<TopBarProps>`
  background-color: ${({ theme }) => theme.colors.white};
  pointer-events: all;
  height: ${({ theme }) => theme.sizes.topBarHeightMobile};
  border-bottom: ${({ theme }) => theme.borders['gray-lighter']};
  padding: 0 0.6rem 0 1.375rem;

  ${Theme.mediaQueries.sm} {
    height: ${({ theme }) => theme.sizes.topBarHeightTablet};
  }

  ${Theme.mediaQueries.md} {
    height: ${({ theme, renderMinimalNav }) =>
      renderMinimalNav
        ? theme.sizes.topBarHeightTablet
        : theme.sizes.sideNavLogoHeightDesktop};
    width: ${({ renderMinimalNav }) =>
      !renderMinimalNav && `calc(100% - 1.5rem * 2)`};
    padding: ${({ renderMinimalNav }) => !renderMinimalNav && 0};
  }
`;

export const HomeButton = styled(Base)`
  img {
    height: 1.5rem;

    ${Theme.mediaQueries.sm} {
      height: 2.625rem;
    }
  }
`;

export const SymptomCheckerButton = styled(Base)`
  && {
    ${Theme.mediaQueries.md} {
      display: none;
    }
  }
`;

const TopBarMenuButton = styled(Base)`
  width: 2.75rem;
  height: 2.75rem;
  border-radius: 4px;
  display: flex;
  justify-content: center;
  align-items: center;

  :hover {
    background-color: ${({ theme }) => theme.colors['gray-lighter']};
  }

  :active {
    ${({ theme }) => theme.hoverStyles.boxShadowBlue};
    border: ${({ theme }) => theme.borders['blue-darker']};
  }
`;

export const CloseSideNavButton = styled(TopBarMenuButton)`
  img {
    width: 1rem;

    ${Theme.mediaQueries.sm} {
      width: 1.3125rem;
    }
  }
`;

export const OpenSideNavButton = styled(TopBarMenuButton)`
  img {
    width: 1.125rem;

    ${Theme.mediaQueries.sm} {
      width: 1.5rem;
    }
  }
`;

const TopBarMenuButtonContainer = styled(Base).withConfig({
  shouldForwardProp: (prop, defaultValidatorFn) =>
    !['display'].includes(prop) && defaultValidatorFn(prop),
})<TopBarMenuButtonContainerProps>`
  ${({ theme }) => theme.transitions['transition']};
  position: absolute;
  right: 0;
`;

export const CloseSideNavButtonContainer = styled(
  TopBarMenuButtonContainer
).withConfig({
  shouldForwardProp: (prop, defaultValidatorFn) =>
    !['menuIsActive'].includes(prop) && defaultValidatorFn(prop),
})`
  opacity: ${({ menuIsActive }) => (menuIsActive ? 1 : 0)};
  pointer-events: ${({ menuIsActive }) => (menuIsActive ? 'auto' : 'none')};
`;

export const OpenSideNavButtonContainer = styled(TopBarMenuButtonContainer)`
  opacity: ${({ menuIsActive }) => (menuIsActive ? 0 : 1)};
  pointer-events: ${({ menuIsActive }) => (menuIsActive ? 'none' : 'auto')};
`;

export const TopNavMenuButtonContainer = styled(Base).withConfig({
  shouldForwardProp: (prop, defaultValidatorFn) =>
    !['display'].includes(prop) && defaultValidatorFn(prop),
})`
  width: 1.7rem;

  ${Theme.mediaQueries.sm} {
    width: 2.5rem;
  }
`;

const calculatNavHeight = (
  currentBreakpoint: string,
  notificationBarIsActive: boolean | null,
  notificationBarHeight: number
) => {
  if (['EXTRA_SMALL', 'SMALL'].includes(currentBreakpoint)) {
    return `calc( 100vh - ${Theme.sizes.topBarHeightMobile} - ${
      notificationBarIsActive ? notificationBarHeight : 0
    }px )`;
  } else if (currentBreakpoint === 'MEDIUM') {
    return `calc( 100vh - ${Theme.sizes.topBarHeightTablet} - ${
      notificationBarIsActive ? notificationBarHeight : 0
    }px )`;
  }

  return `calc( 100vh - ${Theme.sizes.sideNavLogoHeightDesktop} - ${
    notificationBarIsActive ? notificationBarHeight : 0
  }px )`;
};

export const Nav = styled('nav')<NavProps>`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  align-items: start;
  pointer-events: all;
  ${({ theme }) => theme.zIndex('below')};
  background-color: ${({ theme }) => theme.colors.white};
  ${({ theme }) => theme.transitions['transition']};
  height: ${({
    currentBreakpoint,
    notificationBarIsActive,
    notificationBarHeight,
  }) =>
    calculatNavHeight(
      currentBreakpoint,
      notificationBarIsActive,
      notificationBarHeight
    )};
  transform: ${({ menuIsActive }) =>
    menuIsActive ? 'translate3d(0, 0, 0)' : 'translate3d(-120vw, 0, 0)'};

  ${Theme.mediaQueries.md} {
    transform: translate3d(0, 0, 0);
  }
`;

export const MainMenuContainer = styled(Base)<MainMenuContainerProps>`
  height: ${({
    currentBreakpoint,
    notificationBarIsActive,
    notificationBarHeight,
  }) =>
    calculatNavHeight(
      currentBreakpoint,
      notificationBarIsActive,
      notificationBarHeight
    )};
`;

export const MainMenuButton = styled(Base)<MainMenuButtonProps>`
  && {
    font-weight: ${({ isCurrentPath, theme }) =>
      isCurrentPath
        ? theme.typography.weight.bold
        : theme.typography.weight.regular};
  }

  svg {
    margin-left: 0.75rem;
    width: 1.25rem;

    ${Theme.mediaQueries.md} {
      width: 0.5rem;
    }
  }
`;

export const MainMenuSubNav = styled('ul')<MainMenuSubNavProps>`
  ${({ theme }) => theme.transitions['transition']};
  overflow-x: hidden;
  max-height: ${({
    sectionIsExpanded,
    sublistHeights,
    sectionSlug,
    breakpointIsMdUp,
  }) =>
    sectionIsExpanded
      ? `${
          sublistHeights[sectionSlug][breakpointIsMdUp ? 'desktop' : 'mobile']
        }px`
      : 0};
`;

export const AccountSection = styled(Base)`
  border-top: ${({ theme }) => theme.borders['gray-lighter']};
  width: 100%;
  height: ${({ theme }) => theme.sizes.sideNavAccountSectionHeightMobile};

  a {
    margin-left: 1.5rem;
    margin-right: 1.5rem;
  }

  ${Theme.mediaQueries.md} {
    width: calc(100% - 1.5rem * 2);
    padding: 2.25rem 0;
    height: ${({ theme }) => theme.sizes.sideNavAccountSectionHeightDesktop};

    a {
      margin-left: unset;
      margin-right: unset;
    }
  }
`;
